import {
  doesEmployeeNeedBIC,
  NONE_STUDENT_DEFAULT_TAX_CODE,
  STUDENT_DEFAULT_TAX_CODE,
  validators
} from '@vivaldis/common'
import { EmployeeInput, EmployeeStatesEnum } from '@vivaldis/graphql'
import { MaritalStatesEnum } from '@vivaldis/session'
import {
  Formik,
  FormikContextType,
  FormikErrors,
  FormikHelpers,
  useFormikContext
} from 'formik'
import { FC, PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Employee } from '../types/Employee'
import { employeeFormValuesToEmployeeInput } from '../utils/employeeFormValuesToEmployeeInput'

export interface EditEmployeeProfileFormValues {
  // Bank account
  bankAccount: string | null
  bic: string | null
  // Address
  street: string | null
  streetNumber: string | null
  buildingNumber: string | null
  zip: string | null
  city: string | null
  countryId: string | null
  // Additional information
  placeOfBirth: string | null
  countryOfBirthId: string | null
  mobile: string | null
  email: string
  isStudent: boolean
  // Work profile
  maritalState: MaritalStatesEnum | null
  maritalDate: Date | null
  childrenNumber: number | null
  childrenWithDisabilitiesNumber: number | null
  dependentPersonsNumber: number | null
  dependentPersonsWithDisabilitiesNumber: number | null
  taxCode: string | null
  involuntaryPartTime: boolean | null
}

export function useForm(): FormikContextType<EditEmployeeProfileFormValues> {
  return useFormikContext()
}

export interface EmployeeFormProviderProps {
  employee: Employee
  onSubmit: (
    employeeInput: EmployeeInput,
    formikHelpers: FormikHelpers<EditEmployeeProfileFormValues>
  ) => Promise<void>
}

export const FormProvider: FC<PropsWithChildren<EmployeeFormProviderProps>> = ({
  employee,
  onSubmit,
  children
}) => {
  const [t] = useTranslation('web_ui')

  const defaultTaxCode = useMemo(() => {
    const isStudent = employee.isStudent || false
    // The tax should be default (and mandatory) 11,11% for students
    // The tax should be default 23% for non-students
    return employee.workProfile?.taxCode
      ? employee.workProfile?.taxCode
      : isStudent
        ? STUDENT_DEFAULT_TAX_CODE
        : NONE_STUDENT_DEFAULT_TAX_CODE
  }, [employee.isStudent, employee.workProfile?.taxCode])

  const initialFormValues: EditEmployeeProfileFormValues = useMemo(
    () => ({
      // Bank account
      bankAccount: employee.workProfile?.bankAccount || null,
      bic: employee.workProfile?.bic || null,
      // Address
      street: employee.profile?.street || null,
      streetNumber: employee.profile?.streetNumber || null,
      buildingNumber: employee.profile?.buildingNumber || null,
      zip: employee.profile?.zip || null,
      city: employee.profile?.city || null,
      countryId: employee.profile?.country?.id || null,
      // Additional information
      placeOfBirth: employee.profile?.placeOfBirth || null,
      countryOfBirthId: employee.profile?.countryOfBirth?.id || null,
      mobile: employee.mobile,
      email: employee.email,
      isStudent: employee.isStudent,
      // Work profile
      maritalState: employee.workProfile?.maritalState || null,
      maritalDate: employee.workProfile?.maritalDate
        ? new Date(employee.workProfile?.maritalDate)
        : null,
      childrenNumber: employee.workProfile?.childrenNumber || null,
      childrenWithDisabilitiesNumber:
        employee.workProfile?.childrenWithDisabilitiesNumber || null,
      dependentPersonsNumber:
        employee.workProfile?.dependentPersonsNumber || null,
      dependentPersonsWithDisabilitiesNumber:
        employee.workProfile?.dependentPersonsWithDisabilitiesNumber || null,
      taxCode: defaultTaxCode,
      // involuntaryPartTime should be "false" by default
      involuntaryPartTime: employee.workProfile?.involuntaryPartTime ?? false
    }),
    [
      defaultTaxCode,
      employee.email,
      employee.isStudent,
      employee.mobile,
      employee.profile?.buildingNumber,
      employee.profile?.city,
      employee.profile?.country?.id,
      employee.profile?.countryOfBirth?.id,
      employee.profile?.placeOfBirth,
      employee.profile?.street,
      employee.profile?.streetNumber,
      employee.profile?.zip,
      employee.workProfile?.bankAccount,
      employee.workProfile?.bic,
      employee.workProfile?.childrenNumber,
      employee.workProfile?.childrenWithDisabilitiesNumber,
      employee.workProfile?.dependentPersonsNumber,
      employee.workProfile?.dependentPersonsWithDisabilitiesNumber,
      employee.workProfile?.involuntaryPartTime,
      employee.workProfile?.maritalDate,
      employee.workProfile?.maritalState
    ]
  )

  const initialErrors: FormikErrors<EditEmployeeProfileFormValues> =
    useMemo(() => {
      return {
        // Bank account
        ...(!initialFormValues?.bankAccount ? { bankAccount: '' } : {}),
        ...(!initialFormValues?.bic ? { bic: '' } : {}),
        // Address
        ...(!initialFormValues?.street ? { street: '' } : {}),
        ...(!initialFormValues?.streetNumber ? { streetNumber: '' } : {}),
        ...(!initialFormValues?.zip ? { zip: '' } : {}),
        ...(!initialFormValues?.city ? { city: '' } : {}),
        ...(!initialFormValues?.countryId ? { countryId: '' } : {}),
        // Additional information
        ...(!initialFormValues?.placeOfBirth ? { placeOfBirth: '' } : {}),
        ...(!initialFormValues?.countryOfBirthId
          ? { countryOfBirthId: '' }
          : {})
      }
    }, [
      initialFormValues?.bankAccount,
      initialFormValues?.bic,
      initialFormValues?.street,
      initialFormValues?.streetNumber,
      initialFormValues?.zip,
      initialFormValues?.city,
      initialFormValues?.countryId,
      initialFormValues?.placeOfBirth,
      initialFormValues?.countryOfBirthId
    ])

  // We allow saving an empty fields when an employee is not contract ready. In most cases, it's when U initially fill-in information instead of E. But we don't want to allow U to remove valid information
  const isEmployeeAlreadyHaveValidData =
    employee.state === EmployeeStatesEnum.VERIFIED ||
    employee.state === EmployeeStatesEnum.CONTRACT_READY

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        // Bank account
        bankAccount: Yup.string()
          .label(t('edit_employee_general_information_form.bank_account.label'))
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData ||
              !!employee.workProfile?.bankAccount,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          })
          .test(
            'isValidBankAccount',
            t(
              'edit_employee_general_information_form.bank_account.errors.invalid'
            ),
            bankAccount =>
              bankAccount ? validators.isValidIBANNumber(bankAccount) : true
          ),
        bic: Yup.string()
          .label(t('edit_employee_general_information_form.bic.label'))
          .nullable()
          .when('bankAccount', ([bankAccount], schema) => {
            if (doesEmployeeNeedBIC(bankAccount)) {
              return schema.required()
            }
            return schema.notRequired()
          }),
        // Address
        street: Yup.string()
          .label(t('edit_employee_general_information_form.street.label'))
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData || !!employee.profile?.street,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        streetNumber: Yup.string()
          .label(
            t('edit_employee_general_information_form.street_number.label')
          )
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData ||
              !!employee.profile?.streetNumber,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        buildingNumber: Yup.string()
          .label(
            t('edit_employee_general_information_form.building_number.label')
          )
          .nullable()
          .notRequired(),
        zip: Yup.string()
          .label(t('edit_employee_general_information_form.zip.label'))
          .nullable()
          .when({
            is: () => isEmployeeAlreadyHaveValidData || !!employee.profile?.zip,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        city: Yup.string()
          .label(t('edit_employee_general_information_form.city.label'))
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData || !!employee.profile?.city,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        countryId: Yup.string()
          .label(t('edit_employee_general_information_form.country_id.label'))
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData || !!employee.profile?.country?.id,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        // Additional information
        placeOfBirth: Yup.string()
          .label(
            t('edit_employee_general_information_form.place_of_birth.label')
          )
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData ||
              !!employee.profile?.placeOfBirth,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        countryOfBirthId: Yup.string()
          .label(
            t(
              'edit_employee_general_information_form.country_of_birth_id.label'
            )
          )
          .nullable()
          .when({
            is: () =>
              isEmployeeAlreadyHaveValidData ||
              !!employee.profile?.countryOfBirth?.id,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        mobile: Yup.string()
          .label(t('edit_employee_general_information_form.mobile.label'))
          .nullable()
          .when({
            is: () => isEmployeeAlreadyHaveValidData || !!employee.mobile,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        email: Yup.string()
          .label(t('edit_employee_general_information_form.email.label'))
          .nullable()
          .email()
          .required(),
        isStudent: Yup.boolean()
          .label(t('edit_employee_general_information_form.is_student.label'))
          .required(),
        // Work profile
        maritalState: Yup.string()
          .label(
            t('edit_employee_general_information_form.marital_state.label')
          )
          .nullable(),
        maritalDate: Yup.date()
          .label(t('edit_employee_general_information_form.marital_date.label'))
          .nullable(),
        childrenNumber: Yup.number()
          .label(
            t('edit_employee_general_information_form.children_number.label')
          )
          .nullable(),
        childrenWithDisabilitiesNumber: Yup.number()
          .label(
            t(
              'edit_employee_general_information_form.children_with_disabilities_number.label'
            )
          )
          .nullable(),
        dependentPersonsNumber: Yup.number()
          .label(
            t(
              'edit_employee_general_information_form.dependent_persons_number.label'
            )
          )
          .nullable(),
        dependentPersonsWithDisabilitiesNumber: Yup.number()
          .label(
            t(
              'edit_employee_general_information_form.dependent_persons_with_disabilities_number.label'
            )
          )
          .nullable(),
        taxCode: Yup.string()
          .label(t('edit_employee_general_information_form.tax_code.label'))
          .nullable()
          .required(),
        involuntaryPartTime: Yup.boolean()
          .label(
            t(
              'edit_employee_general_information_form.involuntary_part_time.label'
            )
          )
          .nullable()
      }),
    [
      employee.mobile,
      employee.profile?.city,
      employee.profile?.country?.id,
      employee.profile?.countryOfBirth?.id,
      employee.profile?.placeOfBirth,
      employee.profile?.street,
      employee.profile?.streetNumber,
      employee.profile?.zip,
      employee.workProfile?.bankAccount,
      isEmployeeAlreadyHaveValidData,
      t
    ]
  )

  const handleSubmit = useCallback(
    async (
      values: EditEmployeeProfileFormValues,
      formikHelpers: FormikHelpers<EditEmployeeProfileFormValues>
    ) => {
      const employeeInput = employeeFormValuesToEmployeeInput(values)
      await onSubmit(employeeInput, formikHelpers)
      formikHelpers.setSubmitting(false)
    },
    [onSubmit]
  )

  return (
    <Formik<EditEmployeeProfileFormValues>
      initialValues={initialFormValues}
      initialErrors={initialErrors}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
